<template>
  <div class="col-xl-12">
    <div class="card stock-dashboard-card" ref="stockCard">
      <!-- HEADER -->
      <div class="card-header card-no-border">
        <div class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
          <h5>VEHICLE WISE STOCK SUMMARY</h5>

          <div class="d-flex align-items-center gap-2">
            <div
              class="refresh-icon"
              @click="handleRefresh"
              :class="{ disabled: loading }"
              title="Refresh"
            >
              <i :class="loading ? 'fa fa-spinner fa-spin' : 'fa fa-sync'"></i>
            </div>

            <div class="icon-btn" @click="toggleFullScreen" title="Fullscreen">
              <i :class="isFullScreen ? 'fa fa-minimize' : 'fa fa-maximize'"></i>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-start align-items-start flex-wrap gap-2">
          <div class="header-top">
            <div class="d-flex gap-2 flex-wrap">

              <input
                type="date"
                v-model="selectedDate"
                class="form-control"
                style="width: 140px"
                @change="fetchStockData"
              />

              <!-- SKU (multi-select), same pattern as SKU-wise Sales widget -->
              <div
                class="sku-multiselect"
                ref="skuMultiSelect"
              >
                <button
                  type="button"
                  class="form-select sku-select multiselect-toggle"
                  @click="toggleSkuDropdown"
                >
                  <span class="multiselect-toggle-label">{{ skuSelectLabel }}</span>
                  <i
                    class="fa fa-chevron-down multiselect-caret"
                    :class="{ open: skuDropdownOpen }"
                  ></i>
                </button>

                <div v-if="skuDropdownOpen" class="sku-dropdown-panel">
                  <label class="sku-option sku-option-all">
                    <input
                      type="checkbox"
                      :checked="selectedSkus.length === 0"
                      @change="clearSkuSelection"
                    />
                    <span>All SKUs</span>
                  </label>

                  <div class="sku-option-divider"></div>

                  <label
                    v-for="item in items"
                    :key="item.item_id"
                    class="sku-option"
                  >
                    <input
                      type="checkbox"
                      :value="item.item_id"
                      v-model="selectedSkus"
                    />
                    <span>{{ item.item_name }}</span>
                  </label>

                  <div v-if="!items.length" class="sku-option-empty">
                    No SKUs found
                  </div>
                </div>
              </div>

              <select v-model="vehicleStatusFilter" class="form-select" style="width: 130px">
                  <option value="all">All vehicles</option>
                  <option value="active">Active only</option>
                  <option value="inactive">Inactive only</option>
                </select>
            </div>
          </div>
        </div>
      </div>

      <!-- BODY -->
      <div class="card-body pt-0">
        <div v-if="loading" class="spinner-container">
          <div class="spinner"></div>
        </div>

        <div v-else-if="error" class="error-container">
          <p class="error-message">{{ error }}</p>
          <button class="stock-retry-btn" @click="fetchStockData" :disabled="loading">
            Retry
          </button>
        </div>

        <div v-else-if="!visibleVehicles.length || !items.length" class="empty-container">
          <p class="empty-message">No stock data available for the selected filters.</p>
        </div>

        <div v-else class="stock-table-wrap" :style="{ maxHeight: tableMaxHeight + 'px' }">
          <table class="stock-table">
            <thead>
              <!-- Vehicle group row -->
              <tr>
                <th class="item-col sticky-col" rowspan="2">Item</th>

                <th
                  v-for="vehicle in visibleVehicles"
                  :key="vehicle.rep_profile_id"
                  class="vehicle-group-col"
                  :class="{ 'inactive-vehicle': !vehicle.is_active }"
                  colspan="3"
                >
                  {{ vehicle.vehicle_name }}
                  <span v-if="!vehicle.is_active" class="inactive-badge">Inactive</span>
                </th>

                <th class="total-group-col" colspan="3">Total</th>
              </tr>

              <!-- Load / Sale / Balance sub-header -->
              <tr>
                <template v-for="vehicle in visibleVehicles" :key="vehicle.rep_profile_id + '-sub'">
                  <th class="sub-col load-col">Load</th>
                  <th class="sub-col sale-col">Sale</th>
                  <th class="sub-col balance-col">Bal</th>
                </template>

                <th class="sub-col load-col total-sub">Load</th>
                <th class="sub-col sale-col total-sub">Sale</th>
                <th class="sub-col balance-col total-sub">Bal</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="row in filteredRows" :key="row.item_id">
                <td class="item-col sticky-col">{{ row.item_name }}</td>

                <template v-for="cell in visibleCells(row)" :key="cell.rep_profile_id">
                  <td class="qty-cell load-col" :class="{ zero: !cell.load_qty }">
                    {{ formatQty(cell.load_qty) }}
                  </td>
                  <td class="qty-cell sale-col" :class="{ zero: !cell.sale_qty }">
                    {{ formatQty(cell.sale_qty) }}
                  </td>
                  <td
                    class="qty-cell balance-col"
                    :class="{ zero: !cell.balance_qty, negative: cell.balance_qty < 0 }"
                  >
                    {{ formatQty(cell.balance_qty) }}
                  </td>
                </template>

                <td class="qty-cell load-col total-cell">
                  {{ formatQty(rowTotal(row, "load_qty")) }}
                </td>
                <td class="qty-cell sale-col total-cell">
                  {{ formatQty(rowTotal(row, "sale_qty")) }}
                </td>
                <td class="qty-cell balance-col total-cell">
                  {{ formatQty(rowTotal(row, "balance_qty")) }}
                </td>
              </tr>
            </tbody>

            <tfoot>
              <tr>
                <td class="item-col sticky-col">Vehicle Total</td>

                <template v-for="vehicle in visibleVehicles" :key="vehicle.rep_profile_id + '-footer'">
                  <td class="qty-cell load-col footer-cell">
                    {{ formatQty(vehicleTotal(vehicle.rep_profile_id, "load_qty")) }}
                  </td>
                  <td class="qty-cell sale-col footer-cell">
                    {{ formatQty(vehicleTotal(vehicle.rep_profile_id, "sale_qty")) }}
                  </td>
                  <td class="qty-cell balance-col footer-cell">
                    {{ formatQty(vehicleTotal(vehicle.rep_profile_id, "balance_qty")) }}
                  </td>
                </template>

                <td class="qty-cell load-col footer-cell total-cell">
                  {{ formatQty(grandTotal("load_qty")) }}
                </td>
                <td class="qty-cell sale-col footer-cell total-cell">
                  {{ formatQty(grandTotal("sale_qty")) }}
                </td>
                <td class="qty-cell balance-col footer-cell total-cell">
                  {{ formatQty(grandTotal("balance_qty")) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchDistributors,
  fetchVehicleWiseStock,
} from "@/services/dataService";
import { getApiErrorMessage } from "@/utils/apiError";

export default {
  data() {
    const userLabel = localStorage.getItem("user_label");
    return {
      token: localStorage.getItem("token_set"),
      profileId: localStorage.getItem("profile_id"),
      reportingProfileId: localStorage.getItem("reportingL1ProfileId") || "",
      isDistributor: userLabel === "DISTRIBUTOR",
      isDistributorBackOffice: userLabel === "DISTRIBUTOR BACK OFFICE",

      loading: false,
      error: null,
      isFullScreen: false,
      tableMaxHeight: 360,

      distributors: [],
      selectedDistributor: "",
      selectedDate: new Date().toISOString().split("T")[0],

      selectedSkus: [],
      skuDropdownOpen: false,

      vehicles: [],
      items: [],
      rows: [],
      vehicleStatusFilter: "all",

      distributorsLoaded: false,
      distributorRequestPromise: null,
      statsRequestSequence: 0,
      componentActive: true,

      skuDropdownClickOutsideHandler: null,
    };
  },

  mounted() {
    if (this.isDistributor) {
      this.selectedDistributor = this.profileId;
      this.lazyLoadDistributors();
    } else if (this.isDistributorBackOffice) {
      this.selectedDistributor = this.reportingProfileId;
      this.lazyLoadDistributors();
    }

    this.tableMaxHeight = this.isFullScreen
        ? window.innerHeight - 160
        : 360;

    this.fetchStockData();

    document.addEventListener("fullscreenchange", this.handleFullscreenChange);

    this.skuDropdownClickOutsideHandler = (event) => {
      const el = this.$refs.skuMultiSelect;
      if (this.skuDropdownOpen && el && !el.contains(event.target)) {
        this.skuDropdownOpen = false;
      }
    };
    document.addEventListener("click", this.skuDropdownClickOutsideHandler);
  },

  beforeUnmount() {
    this.componentActive = false;
    this.statsRequestSequence += 1;

    document.removeEventListener("fullscreenchange", this.handleFullscreenChange);

    if (this.skuDropdownClickOutsideHandler) {
      document.removeEventListener("click", this.skuDropdownClickOutsideHandler);
      this.skuDropdownClickOutsideHandler = null;
    }
  },

  computed: {
    filteredRows() {
      if (!this.selectedSkus.length) {
        return this.rows;
      }

      return this.rows.filter((row) => this.selectedSkus.includes(row.item_id));
    },

    skuSelectLabel() {
      if (!this.selectedSkus.length) return "All SKUs";

      if (this.selectedSkus.length === 1) {
        const item = this.items.find((i) => i.item_id === this.selectedSkus[0]);
        return item ? item.item_name : "1 SKU selected";
      }

      return `${this.selectedSkus.length} SKUs selected`;
    },

    visibleVehicles() {
      const list = this.vehicles.map((v) => ({
        ...v,
        is_active: v.is_active !== false,
      }));

      const filtered = list.filter((v) => {
        if (this.vehicleStatusFilter === "active") return v.is_active;
        if (this.vehicleStatusFilter === "inactive") return !v.is_active;
        return true;
      });

      return [...filtered].sort((a, b) => Number(b.is_active) - Number(a.is_active));
    },
  },

  methods: {
    handleFullscreenChange() {
      this.isFullScreen = !!document.fullscreenElement;

        this.$nextTick(() => {
            this.tableMaxHeight = this.isFullScreen
            ? window.innerHeight - 160
            : 360;
        });
    },

    async lazyLoadDistributors() {
      if (this.distributorsLoaded) {
        return;
      }

      if (this.distributorRequestPromise) {
        await this.distributorRequestPromise;
        return;
      }

      this.distributorRequestPromise = this.fetchDistributorList();

      try {
        const loaded = await this.distributorRequestPromise;

        if (loaded) {
          this.distributorsLoaded = true;
        }
      } finally {
        this.distributorRequestPromise = null;
      }
    },

    async fetchDistributorList() {
      try {
        const response = await fetchDistributors(this.profileId, this.token);

        if (!this.componentActive) {
          return false;
        }

        this.distributors = Array.isArray(response.data?.data)
          ? response.data.data
          : [];

        return true;
      } catch (error) {
        console.error(
          "Distributor list API failed:",
          error?.response?.status,
          error?.response?.data || error
        );

        return false;
      }
    },

    handleRefresh() {
      if (this.loading) {
        return;
      }

      this.fetchStockData();
    },

    toggleSkuDropdown() {
      this.skuDropdownOpen = !this.skuDropdownOpen;
    },

    clearSkuSelection() {
      this.selectedSkus = [];
    },

    async fetchStockData() {
      const requestId = ++this.statsRequestSequence;

      try {
        this.loading = true;
        this.error = null;

        const payload = {
          profile_id: this.selectedDistributor || this.profileId,
          date: this.selectedDate,
        };

        const response = await fetchVehicleWiseStock(payload, this.token);

        if (!this.componentActive || requestId !== this.statsRequestSequence) {
          return;
        }

        if (response.data.success) {
          this.vehicles = response.data.vehicles || [];
          this.items = response.data.items || [];
          this.rows = response.data.data || [];

          // Drop any selected SKUs that no longer exist in the new result set
          const validItemIds = new Set(this.items.map((i) => i.item_id));
          this.selectedSkus = this.selectedSkus.filter((id) => validItemIds.has(id));
        } else {
          this.vehicles = [];
          this.items = [];
          this.rows = [];
          this.error = response.data.message || "No data available";
        }
      } catch (error) {
        if (!this.componentActive || requestId !== this.statsRequestSequence) {
          return;
        }

        console.error(
          "Vehicle stock API failed:",
          error?.response?.status,
          error?.response?.data || error
        );

        this.error = getApiErrorMessage(error);
      } finally {
        if (this.componentActive && requestId === this.statsRequestSequence) {
          this.loading = false;
        }
      }
    },

    visibleCells(row) {
      const cellByRep = new Map((row.stock || []).map((c) => [c.rep_profile_id, c]));
      return this.visibleVehicles
        .map((v) => cellByRep.get(v.rep_profile_id))
        .filter(Boolean);
    },

    rowTotal(row, field) {
      return this.visibleCells(row).reduce(
        (sum, cell) => sum + Number(cell[field] || 0),
        0
      );
    },

    vehicleTotal(repProfileId, field) {
      return this.filteredRows.reduce((sum, row) => {
        const cell = (row.stock || []).find(
          (c) => c.rep_profile_id === repProfileId
        );
        return sum + Number(cell?.[field] || 0);
      }, 0);
    },

    grandTotal(field) {
      return this.filteredRows.reduce((sum, row) => sum + this.rowTotal(row, field), 0);
    },

    formatQty(value) {
      const num = Number(value || 0);
      return num % 1 === 0
        ? num.toLocaleString()
        : num.toLocaleString(undefined, { maximumFractionDigits: 2 });
    },

    toggleFullScreen() {
      const element = this.$refs.stockCard;

      if (!document.fullscreenElement) {
        element.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    },
  },
};
</script>

<style scoped>
.stock-dashboard-card {
  height: 100%;
  border: 1px solid #e8edf4;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.stock-dashboard-card .card-header {
  padding: 10px 14px 8px !important;
  border-bottom: 1px solid #eef2f7;
  background: #ffffff;
}

.stock-dashboard-card .card-header h5 {
  margin: 0;
  color: #0f172a;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: -0.01em;
}

.stock-dashboard-card .card-body {
  padding: 8px 14px 12px !important;
}

.header-top {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

select {
  width: auto;
  font-size: 15px;
}

select option {
  font-size: 15px;
}

.refresh-icon,
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  color: #6c757d;
  transition: all 0.2s ease;
  border: none;
  background: transparent;
  padding: 0;
}

.refresh-icon:hover,
.icon-btn:hover {
  background: #f1f3f5;
  color: #000;
}

.refresh-icon.disabled {
  pointer-events: none;
  opacity: 0.5;
}

.spinner-container {
  display: flex;
  justify-content: center;
  height: 200px;
  align-items: center;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #6c5ce7;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

.error-container,
.empty-container {
  text-align: center;
  padding: 30px 10px;
}

.error-message {
  color: red;
  font-weight: bold;
}

.empty-message {
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
}

.stock-retry-btn {
  min-height: 30px;
  margin-top: 8px;
  padding: 5px 12px;
  border: 1px solid #dbe2ea;
  border-radius: 8px;
  background: #ffffff;
  color: #334155;
  font-size: 11.5px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.stock-retry-btn:hover:not(:disabled) {
  border-color: #7064f5;
  color: #7064f5;
  background: #f8f7ff;
}

.stock-retry-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Table */
.stock-table-wrap {
  overflow-x: auto;
  overflow-y: auto;
  border: 1px solid #e8edf4;
  border-radius: 10px;
}

.stock-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.stock-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f8fafc;
  color: #334155;
  font-weight: 800;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 8px 8px;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  text-align: center;
}

.stock-table thead tr:first-child th {
  top: 0;
}

.stock-table thead tr:last-child th {
  top: 33px;
}

.stock-table thead th.item-col {
  text-align: left;
}

.vehicle-group-col {
  background: #f1f5f9;
  border-left: 1px solid #e2e8f0;
}

.total-group-col {
  background: #f1edff;
  color: #6c5ce7;
  border-left: 2px solid #d8ccff;
}

.sub-col {
  font-size: 10px;
  font-weight: 700;
  min-width: 58px;
}

.sub-col.load-col {
  border-left: 1px solid #e2e8f0;
}

.sub-col.total-sub {
  background: #f1edff;
  color: #6c5ce7;
}

.stock-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
}

.item-col {
  text-align: left;
  font-weight: 700;
  color: #0f172a;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #ffffff;
  min-width: 180px;
}

.stock-table thead th.sticky-col {
  z-index: 4;
  background: #f8fafc;
}

.qty-cell {
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: #1f2937;
}

.qty-cell.load-col {
  border-left: 1px solid #f1f5f9;
}

.qty-cell.zero {
  color: #cbd5e1;
}

.qty-cell.negative {
  color: #ef4444;
  font-weight: 700;
}

.total-cell {
  font-weight: 800;
  color: #6c5ce7;
  background: #f8f7ff;
}

.footer-cell {
  font-weight: 800;
  background: #f8fafc;
  color: #0f172a;
}

.stock-table tfoot td {
  position: sticky;
  bottom: 0;
  z-index: 3;
}

tfoot tr td.sticky-col {
  z-index: 5;
  background: #f8fafc;
}

.sku-multiselect {
  position: relative;
}

.multiselect-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  text-align: left;
  background-color: #fff;
  cursor: pointer;
}

.multiselect-toggle-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.multiselect-caret {
  font-size: 10px;
  color: #6c757d;
  transition: transform 0.15s ease;
  flex: 0 0 auto;
}

.multiselect-caret.open {
  transform: rotate(180deg);
}

.multiselect-toggle.form-select {
  background-image: none !important;
  padding-right: 12px;
}

.sku-select {
  width: 170px !important;
}

.sku-dropdown-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 60;
  width: 220px;
  max-height: 260px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
  padding: 6px;
}

.sku-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 8px;
  border-radius: 6px;
  font-size: 13.5px;
  color: #334155;
  cursor: pointer;
}

.sku-option:hover {
  background: #f1f5f9;
}

.sku-option input[type="checkbox"] {
  flex: 0 0 auto;
  cursor: pointer;
}

.sku-option span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sku-option-all {
  font-weight: 700;
}

.sku-option-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 4px 2px;
}

.sku-option-empty {
  padding: 10px 8px;
  font-size: 13px;
  color: #94a3b8;
  text-align: center;
}

.vehicle-group-col.inactive-vehicle {
  background: #fef2f2;
  color: #dc2626;
}

.inactive-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  background: #fee2e2;
  color: #b91c1c;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.02em;
  vertical-align: middle;
}
</style>